import { getSafeAuthRedirect } from "@/utils/authRedirect";

const ATTEMPT_KEY = "googleLoginAttempt";
const RESULT_PREFIX = "googleLoginResult:";
const MAX_AGE = 15 * 60 * 1000;

export function getGoogleLoginAttempt() {
  try {
    const attempt = JSON.parse(sessionStorage.getItem(ATTEMPT_KEY));
    if (
      typeof attempt?.id === "string" &&
      /^[a-f0-9]{32}$/.test(attempt.id) &&
      Number.isFinite(attempt.createdAt) &&
      Date.now() - attempt.createdAt >= 0 &&
      Date.now() - attempt.createdAt < MAX_AGE
    ) {
      return attempt;
    }
  } catch {
    // Storage may be unavailable in restricted browser contexts.
  }
  return null;
}

function clearAttempt(id) {
  try {
    if (getGoogleLoginAttempt()?.id === id) {
      sessionStorage.removeItem(ATTEMPT_KEY);
    }
    localStorage.removeItem(RESULT_PREFIX + id);
  } catch {
    // Cleanup must not interrupt authentication.
  }
}

/** Start tracking only after a user initiates login. Never exchange bearer tokens. */
export function startGoogleLogin({ redirectTo, usePopupMode, onSuccess }) {
  const id = Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
  const attempt = {
    id,
    createdAt: Date.now(),
    redirectTo: getSafeAuthRedirect(redirectTo),
    usePopupMode,
  };
  let stored = false;
  try {
    sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(attempt));
    stored = true;
  } catch {
    console.warn(
      "Google login will use a full-page redirect: storage unavailable."
    );
  }
  const url = `${process.env.VUE_APP_API_URL}/api/oauth/connect/google`;
  let popup = null;
  let interval;
  let active = true;

  function stop() {
    active = false;
    window.removeEventListener("message", onMessage);
    window.removeEventListener("storage", onStorage);
    clearInterval(interval);
    clearAttempt(id);
    try {
      popup?.close();
    } catch {
      // A provider's opener policy may prevent access to the popup.
    }
  }

  function complete(data) {
    if (
      !active ||
      Date.now() - attempt.createdAt >= MAX_AGE ||
      data?.type !== "googleLoginSuccess" ||
      data.transactionId !== id
    ) {
      return;
    }
    stop();
    onSuccess({
      redirectTo: getSafeAuthRedirect(data.redirectTo, attempt.redirectTo),
      isMergedAccount: data.isMergedAccount === true,
    });
  }

  function onMessage(event) {
    if (event.origin !== window.location.origin || event.source !== popup)
      return;
    complete(event.data);
  }

  function readResult(value) {
    try {
      complete(JSON.parse(value));
    } catch {
      // Ignore unrelated or malformed storage notifications.
    }
  }

  function onStorage(event) {
    if (
      event.storageArea === localStorage &&
      event.key === RESULT_PREFIX + id
    ) {
      readResult(event.newValue);
    }
  }

  if (usePopupMode && stored) {
    window.addEventListener("message", onMessage);
    window.addEventListener("storage", onStorage);
    // A fresh same-origin blank window inherits this attempt's sessionStorage.
    // This also keeps the storage fallback working if Google severs the opener.
    popup = window.open(
      "about:blank",
      `googleAuth-${id}`,
      "width=500,height=600,scrollbars=yes,resizable=yes"
    );
    if (popup) {
      popup.location.href = url;
      interval = setInterval(() => {
        if (Date.now() - attempt.createdAt >= MAX_AGE) {
          stop();
          return;
        }
        try {
          readResult(localStorage.getItem(RESULT_PREFIX + id));
        } catch {
          // postMessage remains available when localStorage is blocked.
        }
      }, 1000);
      return stop;
    }
    window.removeEventListener("message", onMessage);
    window.removeEventListener("storage", onStorage);
  }

  // A blocked popup falls back to the existing full-page OAuth flow.
  attempt.usePopupMode = false;
  try {
    sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(attempt));
  } catch {
    // The callback can still establish the session and return to the home page.
  }
  window.location.href = url;
  return () => {};
}

export function notifyGoogleLoginSuccess(attempt, redirectTo, isMergedAccount) {
  const result = {
    type: "googleLoginSuccess",
    transactionId: attempt.id,
    redirectTo: getSafeAuthRedirect(redirectTo),
    isMergedAccount,
  };
  try {
    // Only a completion signal is persisted; the session stays in its cookie.
    localStorage.setItem(RESULT_PREFIX + attempt.id, JSON.stringify(result));
  } catch {
    console.warn("Google login storage notification unavailable.");
  }
  try {
    if (window.opener) {
      window.opener.postMessage(result, window.location.origin);
    }
  } catch {
    // The storage notification also supports a severed or inaccessible opener.
  }
  try {
    sessionStorage.removeItem(ATTEMPT_KEY);
  } catch {
    // The initiating window also expires and cleans up its attempt.
  }
}
